import { getApiBase, getToken } from "./auth.ts";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json" };
  const token = getToken();
  if (token) headers["authorization"] = `Bearer ${token}`;
  if (body !== undefined) headers["content-type"] = "application/json";

  const res = await fetch(getApiBase() + path, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : {};
  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string } }).error ?? {};
    throw new ApiError(res.status, err.code ?? "ERROR", err.message ?? res.statusText);
  }
  return json as T;
}

// --- minimal response shapes (the orchestrator's wire contract) ---

export interface HealthResponse {
  status: string;
  time: string;
}
export interface ReadyResponse {
  status: string;
  checks: Record<string, string>;
  time: string;
}
export interface Policy {
  id: string;
  version: number;
  org: string;
  subjects: { id: string; type: string }[];
  rules: unknown[];
  createdAt: string;
  updatedAt: string;
}
export interface Intent {
  id: string;
  org: string;
  policyId: string;
  subject: string;
  action: string;
  chainId: string;
  params: Record<string, unknown>;
  status: string;
  requiredApprovals: number;
  approvals: { by: string }[];
  idempotencyKey: string;
  txHash?: string;
  failureReason?: string;
  createdAt: string;
  updatedAt: string;
}
export interface AuditEntry {
  seq: number;
  type: string;
  at: string;
  org?: string;
  hash?: string;
  [k: string]: unknown;
}
export interface VerifyResponse {
  count: number;
  headHash: string;
}
export interface Anchor {
  seq: number;
  headHash: string;
  chainId?: string;
  txHash?: string;
  [k: string]: unknown;
}

export const api = {
  health: () => call<HealthResponse>("GET", "/healthz"),
  ready: () => call<ReadyResponse>("GET", "/readyz"),
  createPolicy: (body: unknown) => call<Policy>("POST", "/v1/policies", body),
  getPolicy: (id: string) => call<Policy>("GET", `/v1/policies/${encodeURIComponent(id)}`),
  submitIntent: (body: unknown) => call<Intent>("POST", "/v1/intents", body),
  getIntent: (id: string) => call<Intent>("GET", `/v1/intents/${encodeURIComponent(id)}`),
  approvalMessage: (id: string, by: string) =>
    call<{ intentId: string; by: string; message: string }>(
      "GET",
      `/v1/intents/${encodeURIComponent(id)}/approval-message?by=${encodeURIComponent(by)}`,
    ),
  approveIntent: (id: string, body: { by: string; signature: string; scheme?: "ed25519" | "secp256k1" }) =>
    call<Intent>("POST", `/v1/intents/${encodeURIComponent(id)}/approvals`, body),
  listAudit: () => call<{ entries: AuditEntry[] }>("GET", "/v1/audit"),
  verifyAudit: () => call<VerifyResponse>("GET", "/v1/audit/verify"),
  listAnchors: () => call<{ anchors: Anchor[] }>("GET", "/v1/audit/anchors"),
};
